import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  TextField,
  Button,
  Grid,
  CircularProgress,
  Typography,
  Box,
  InputAdornment,
  IconButton,
  FormHelperText,
  FormControl,
  Checkbox,
  FormControlLabel,
  Link,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { registerEnterpriseClient } from '../../services/ClientService';

/**
 * Formulário de registro para cliente Empresarial (PJ)
 */
export default function EnterpriseRegistrationForm({ onSuccess, onError }) {
  // Estado do formulário
  const [formData, setFormData] = useState({
    email: '',
    login_name: '',
    password: '',
    confirmPassword: '',
    cnpj: '',
    company_name: '',
    trade_name: '',
    phone_number: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    legal_representative_name: '',
    legal_representative_email: '',
    check_licence_agreement: false,
    check_privacy_statement: false,
  });

  // Estado de validação do formulário
  const [errors, setErrors] = useState({});

  // Estado de exibição de senha
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Estado de loading
  const [loading, setLoading] = useState(false);

  const baseFontSize = '0.9375rem'; // ~15px
  const headingFontSize = '1.1875rem'; // ~19px

  // Manipular mudanças no formulário
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (type === 'checkbox') {
      setFormData({ ...formData, [name]: checked });
      setErrors((prev) => {
        const { [name]: _removed, ...rest } = prev;
        return rest;
      });
      return;
    }

    // Formatação específica para CNPJ (apenas números)
    if (name === 'cnpj') {
      const cnpjDigits = value.replace(/\D/g, '');
      setFormData({ ...formData, cnpj: cnpjDigits.substring(0, 14) });
      return;
    }

    // Formatação específica para CEP (apenas números)
    if (name === 'zip_code') {
      const zipDigits = value.replace(/\D/g, '');
      setFormData({ ...formData, zip_code: zipDigits.substring(0, 8) });
      return;
    }

    // Formatação para telefone (apenas números)
    if (name === 'phone_number') {
      const phoneDigits = value.replace(/\D/g, '');
      setFormData({ ...formData, phone_number: phoneDigits });
      return;
    }

    // Para outros campos, atualiza normalmente
    setFormData({ ...formData, [name]: value });
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFormData({ ...formData, [name]: checked });
    setErrors((prevErrors) => {
      const { [name]: _discarded, ...rest } = prevErrors;
      return rest;
    });
  };

  // Função para verificar os requisitos de senha
  const checkPasswordRequirements = (password) => {
    // Sanitização da senha
    const sanitizedPassword = password ? String(password) : '';

    // Cada requisito com seu status (atendido ou não)
    const requirementsStatus = {
      length: {
        text: 'Deve ter pelo menos 10 caracteres',
        met: sanitizedPassword.length >= 10,
      },
      uppercase: {
        text: 'Deve conter pelo menos uma letra maiúscula',
        met: /[A-Z]/.test(sanitizedPassword),
      },
      lowercase: {
        text: 'Deve conter pelo menos uma letra minúscula',
        met: /[a-z]/.test(sanitizedPassword),
      },
      number: {
        text: 'Deve conter pelo menos um número',
        met: /[0-9]/.test(sanitizedPassword),
      },
      symbol: {
        text: 'Deve conter pelo menos um símbolo especial',
        met: /[!@#$%^&*()_+\-=[\]{};:"'\\|,.<>/? ]/.test(sanitizedPassword),
      },
    };

    // Verificar se todos os requisitos foram atendidos
    const allRequirementsMet = Object.values(requirementsStatus).every((req) => req.met);

    return {
      requirementsStatus,
      allRequirementsMet,
      failedCount: Object.values(requirementsStatus).filter((req) => !req.met).length,
    };
  };

  // Manipulador para quando o campo de senha perde o foco
  const handlePasswordBlur = () => {
    // Verificar todos os requisitos da senha
    const passwordCheck = checkPasswordRequirements(formData.password);

    // Atualizar erros ou sucesso
    setErrors((prevErrors) => ({
      ...prevErrors,
      password: passwordCheck.failedCount > 0 ? passwordCheck : 'valid',
    }));
  };

  // Manipulador para quando o campo de confirmação de senha perde o foco
  const handleConfirmPasswordBlur = () => {
    if (!formData.confirmPassword) {
      // Se o campo está vazio, não mostra mensagem
      setErrors((prevErrors) => {
        const { _confirmPassword, ...rest } = prevErrors;
        return rest;
      });
    } else if (formData.password !== formData.confirmPassword) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        confirmPassword: 'As senhas não conferem',
      }));
    } else {
      // As senhas conferem
      setErrors((prevErrors) => ({
        ...prevErrors,
        confirmPassword: 'valid',
      }));
    }
  };

  // Função de validação
  const validateForm = () => {
    const newErrors = {};

    // Validar email
    if (!formData.email) {
      newErrors.email = 'O email é obrigatório';
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.email)) {
      newErrors.email = 'Email inválido';
    }

    // Validar login_name
    if (!formData.login_name) {
      newErrors.login_name = 'O nome de login é obrigatório';
    } else if (formData.login_name.length < 3) {
      newErrors.login_name = 'O nome de login deve ter pelo menos 3 caracteres';
    }

    // Validar senha - checando todos os requisitos de uma vez
    const passwordCheck = checkPasswordRequirements(formData.password);
    if (!passwordCheck.allRequirementsMet) {
      newErrors.password = passwordCheck;
    }

    // Validar confirmação de senha
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'As senhas não conferem';
    }

    // Validar CNPJ
    if (!formData.cnpj) {
      newErrors.cnpj = 'O CNPJ é obrigatório';
    } else if (formData.cnpj.length !== 14) {
      newErrors.cnpj = 'CNPJ deve ter 14 dígitos';
    }

    // Validar razão social
    if (!formData.company_name) {
      newErrors.company_name = 'A razão social é obrigatória';
    } else if (formData.company_name.length < 3) {
      newErrors.company_name = 'A razão social deve ter pelo menos 3 caracteres';
    }

    // Validar email do representante legal, se fornecido
    if (
      formData.legal_representative_email &&
      !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.legal_representative_email)
    ) {
      newErrors.legal_representative_email = 'Email inválido';
    }

    if (!formData.check_licence_agreement) {
      newErrors.check_licence_agreement =
        'É necessário aceitar os termos e condições e licenciamento.';
    }

    if (!formData.check_privacy_statement) {
      newErrors.check_privacy_statement =
        'É necessário aceitar o uso de dados sensíveis conforme a LGPD.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Formatar CNPJ para exibição
  const formatCNPJ = (cnpj) => {
    if (!cnpj) return '';
    return cnpj.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  };

  // Formatar CEP para exibição
  const formatZipCode = (zipCode) => {
    if (!zipCode) return '';
    return zipCode.replace(/(\d{5})(\d{3})/, '$1-$2');
  };

  // Formatar telefone para exibição
  const formatPhoneNumber = (phone) => {
    if (!phone) return '';
    if (phone.length <= 10) {
      // Formato (XX) XXXX-XXXX para telefones fixos
      return phone.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
    }
    // Formato (XX) XXXXX-XXXX para celulares
    return phone.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
  };

  // Enviar formulário
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validar formulário
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      // Criar objeto de dados para envio à API
      const clientData = { ...formData };
      delete clientData.confirmPassword;

      // Registrar cliente
      const _result = await registerEnterpriseClient(clientData);

      // Notificar sucesso
      onSuccess(`Registro realizado com sucesso! Você será redirecionado para a página de login.`);
    } catch (error) {
      // Notificar erro
      onError(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{
        '& .MuiInputBase-input': { fontSize: baseFontSize },
        '& .MuiInputLabel-root': { fontSize: baseFontSize },
        '& .MuiFormHelperText-root': { fontSize: '0.8125rem' },
        '& .MuiButton-root': { fontSize: baseFontSize },
        '& .MuiFormControlLabel-label': { fontSize: baseFontSize },
      }}
    >
      <Typography variant="h6" gutterBottom sx={{ fontSize: headingFontSize }}>
        Dados da Empresa
      </Typography>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12}>
          <TextField
            label="Email"
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            fullWidth
            required
            autoComplete="off"
            error={!!errors.email}
            helperText={errors.email || ''}
            inputProps={{ maxLength: 100 }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            label="Nome de Login"
            name="login_name"
            value={formData.login_name}
            onChange={handleChange}
            fullWidth
            required
            autoComplete="off"
            error={!!errors.login_name}
            helperText={errors.login_name || ''}
            inputProps={{ maxLength: 100 }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            label="CNPJ"
            name="cnpj"
            value={formData.cnpj}
            onChange={handleChange}
            fullWidth
            required
            error={!!errors.cnpj}
            helperText={errors.cnpj || ''}
            placeholder="Apenas números"
            inputProps={{ inputMode: 'numeric', maxLength: 14 }}
            InputProps={{
              endAdornment: formData.cnpj && (
                <InputAdornment position="end">
                  <Typography variant="body2" color="textSecondary">
                    {formatCNPJ(formData.cnpj)}
                  </Typography>
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            label="Razão Social"
            name="company_name"
            value={formData.company_name}
            onChange={handleChange}
            fullWidth
            required
            error={!!errors.company_name}
            helperText={errors.company_name || ''}
            inputProps={{ maxLength: 150 }}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            label="Nome Fantasia"
            name="trade_name"
            value={formData.trade_name}
            onChange={handleChange}
            fullWidth
            inputProps={{ maxLength: 150 }}
          />
        </Grid>
      </Grid>

      <Typography variant="h6" gutterBottom sx={{ fontSize: headingFontSize }}>
        Senha
      </Typography>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth variant="outlined">
            <TextField
              label="Senha"
              name="password"
              type={showPassword ? 'text' : 'password'}
              value={formData.password}
              onChange={handleChange}
              onBlur={handlePasswordBlur}
              fullWidth
              required
              autoComplete="new-password"
              error={errors.password && errors.password !== 'valid'}
              color={errors.password === 'valid' ? 'success' : undefined}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton edge="end" onClick={() => setShowPassword(!showPassword)}>
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            {errors.password && (
              <>
                {errors.password === 'valid' ? (
                  <FormHelperText sx={{ color: 'green' }}>Senha válida</FormHelperText>
                ) : errors.password.requirementsStatus ? (
                  <Box sx={{ mt: 1, mb: 1 }}>
                    <FormHelperText>Requisitos de senha:</FormHelperText>
                    <Box component="ul" sx={{ m: 0, pl: 2 }}>
                      {Object.values(errors.password.requirementsStatus).map(
                        (requirement, index) => (
                          <Box
                            component="li"
                            key={index}
                            sx={{
                              color: requirement.met ? 'green' : 'error.main',
                              fontSize: '0.75rem',
                              lineHeight: '1.66',
                              mb: 0.5,
                            }}
                          >
                            {requirement.text}
                          </Box>
                        ),
                      )}
                    </Box>
                  </Box>
                ) : (
                  <FormHelperText error>{errors.password}</FormHelperText>
                )}
              </>
            )}
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControl fullWidth variant="outlined">
            <TextField
              label="Confirme a Senha"
              name="confirmPassword"
              type={showConfirmPassword ? 'text' : 'password'}
              value={formData.confirmPassword}
              onChange={handleChange}
              onBlur={handleConfirmPasswordBlur}
              fullWidth
              required
              autoComplete="new-password"
              error={errors.confirmPassword && errors.confirmPassword !== 'valid'}
              color={errors.confirmPassword === 'valid' ? 'success' : undefined}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      edge="end"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            {errors.confirmPassword &&
              (errors.confirmPassword === 'valid' ? (
                <FormHelperText sx={{ color: 'green' }}>Confirmação confere</FormHelperText>
              ) : (
                <FormHelperText error>{errors.confirmPassword}</FormHelperText>
              ))}
          </FormControl>
        </Grid>
      </Grid>

      <Typography variant="h6" gutterBottom sx={{ fontSize: headingFontSize }}>
        Representante Legal (Opcional)
      </Typography>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <TextField
            label="Nome do Representante Legal"
            name="legal_representative_name"
            value={formData.legal_representative_name}
            onChange={handleChange}
            fullWidth
            inputProps={{ maxLength: 150 }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            label="Email do Representante Legal"
            type="email"
            name="legal_representative_email"
            value={formData.legal_representative_email}
            onChange={handleChange}
            fullWidth
            error={!!errors.legal_representative_email}
            helperText={errors.legal_representative_email || ''}
            inputProps={{ maxLength: 100 }}
          />
        </Grid>
      </Grid>

      <Typography variant="h6" gutterBottom sx={{ fontSize: headingFontSize }}>
        Informações de Contato (Opcional)
      </Typography>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <TextField
            label="Telefone"
            name="phone_number"
            value={formData.phone_number}
            onChange={handleChange}
            fullWidth
            placeholder="Apenas números"
            inputProps={{ inputMode: 'numeric', maxLength: 11 }}
            InputProps={{
              endAdornment: formData.phone_number && (
                <InputAdornment position="end">
                  <Typography variant="body2" color="textSecondary">
                    {formatPhoneNumber(formData.phone_number)}
                  </Typography>
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            label="CEP"
            name="zip_code"
            value={formData.zip_code}
            onChange={handleChange}
            fullWidth
            placeholder="Apenas números"
            inputProps={{ inputMode: 'numeric', maxLength: 8 }}
            InputProps={{
              endAdornment: formData.zip_code && (
                <InputAdornment position="end">
                  <Typography variant="body2" color="textSecondary">
                    {formatZipCode(formData.zip_code)}
                  </Typography>
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            label="Endereço"
            name="address"
            value={formData.address}
            onChange={handleChange}
            fullWidth
            inputProps={{ maxLength: 150 }}
          />
        </Grid>

        <Grid item xs={12} md={8}>
          <TextField
            label="Cidade"
            name="city"
            value={formData.city}
            onChange={handleChange}
            fullWidth
            inputProps={{ maxLength: 100 }}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <TextField
            label="Estado"
            name="state"
            value={formData.state}
            onChange={handleChange}
            fullWidth
            inputProps={{ maxLength: 2 }}
          />
        </Grid>
      </Grid>

      <Typography variant="h6" gutterBottom sx={{ fontSize: headingFontSize }}>
        Condições Gerais
      </Typography>

      <Typography sx={{ fontSize: '0.6875rem', mb: 2 }}>
        Leia nossos termos e condições gerais e privacidade{' '}
        <Link
          href="/docs/20251029_Termos_de_Uso_GreenNexus.pdf"
          target="_blank"
          rel="noopener noreferrer"
        >
          aqui
        </Link>
        , antes de aceitar os checkboxes abaixo.
      </Typography>

      <Box sx={{ mb: 3 }}>
        <FormControl
          required
          error={!!errors.check_licence_agreement}
          component="fieldset"
          variant="standard"
          sx={{ mb: 2 }}
        >
          <FormControlLabel
            control={
              <Checkbox
                name="check_licence_agreement"
                checked={formData.check_licence_agreement}
                onChange={handleCheckboxChange}
              />
            }
            label="Termos e condições e licenciamento"
          />
          {errors.check_licence_agreement && (
            <FormHelperText>{errors.check_licence_agreement}</FormHelperText>
          )}
        </FormControl>

        <FormControl
          required
          error={!!errors.check_privacy_statement}
          component="fieldset"
          variant="standard"
        >
          <FormControlLabel
            control={
              <Checkbox
                name="check_privacy_statement"
                checked={formData.check_privacy_statement}
                onChange={handleCheckboxChange}
              />
            }
            label="Uso de dados sensíveis e protegidos pela LGPD"
          />
          {errors.check_privacy_statement && (
            <FormHelperText>{errors.check_privacy_statement}</FormHelperText>
          )}
        </FormControl>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
        <Button
          type="submit"
          variant="contained"
          color="primary"
          size="large"
          disabled={loading}
          sx={{ minWidth: 200 }}
        >
          {loading ? <CircularProgress size={24} /> : 'Registrar'}
        </Button>
      </Box>
    </Box>
  );
}

EnterpriseRegistrationForm.propTypes = {
  onSuccess: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired,
};
